import {
  VirtualTable,
  TableFixedColumns,
} from '@devexpress/dx-react-grid-material-ui';
import { useTheme } from '@mui/material';
import React from 'react';
import { TruncatedIdCell } from './TruncatedIdCell';

export const DefaultTableCell = ({ ...cellProps }) => {
  const onClick = cellProps.column.disableExpandRow
    ? (e: any) => e.stopPropagation()
    : null;
  return (
    // @ts-expect-error TS(2739): Type '{ onClick: ((e: any) => any) | null; classNa... Remove this comment to see the full error message
    <VirtualTable.Cell
      onClick={onClick}
      className={`${cellProps.column?.className || ''}`}
      data-qa={`TABLE_CELL:${cellProps.column?.name}`}
      {...cellProps}
    />
  );
};

export const IdTableCell = ({ ...cellProps }) => {
  const onClick = cellProps.column.disableExpandRow
    ? (e: any) => e.stopPropagation()
    : null;

  // Get the ID value from the cell - try multiple sources
  const idValue =
    cellProps.value ||
    cellProps.children ||
    cellProps.tableRow?.data?.[cellProps.column?.name] ||
    '';

  // A column with its own `getCellValue` can return a React element (a link, a
  // tooltip-wrapped value) rather than a primitive. Stringifying one renders
  // "[object Object]", so elements pass straight through and only primitive
  // ids get the truncate + copy treatment.
  const content = React.isValidElement(idValue) ? (
    idValue
  ) : (
    <TruncatedIdCell idValue={String(idValue)} />
  );

  return (
    // @ts-expect-error TS(2739): Type '{ onClick: ((e: any) => any) | null; classNa... Remove this comment to see the full error message
    <VirtualTable.Cell
      onClick={onClick}
      className={`${cellProps.column?.className || ''}`}
      data-qa={`TABLE_CELL:${cellProps.column?.name}`}
      {...cellProps}
    >
      {content}
    </VirtualTable.Cell>
  );
};

export const FixedColumnCell = ({ ...props }) => {
  const { tableColumn, showLeftDivider, showRightDivider } = props;
  const { palette } = useTheme();
  const color =
    props.tableRow.key === 'Symbol(heading)'
      ? palette.background.tableHeader
      : 'inherit';

  // The fixed/scrolling boundary divider is drawn here rather than left to
  // dx-react-grid: the plugin derives it from `palette.divider` and then runs
  // it through `lighten(alpha(divider, 1), 0.88)`, which resolves to ~#f3f4f5
  // on our white rows — invisible (FIR-20966). The plugin tells us which side
  // needs the line, and an inline style outranks its emotion class.
  const divider = `1px solid ${palette.border.tableFixedColumn}`;

  return (
    // @ts-expect-error TS(2740): Type '{ style: any; className: string; }' is missi... Remove this comment to see the full error message
    <TableFixedColumns.Cell
      className={`${tableColumn.column?.className || ''}`}
      {...props}
      style={{
        backgroundColor: color,
        ...props.style,
        ...(showLeftDivider ? { borderLeft: divider } : {}),
        ...(showRightDivider ? { borderRight: divider } : {}),
        ...tableColumn?.column?.style,
      }}
    />
  );
};

/**
 * Helper function to create an ID column configuration with copy to clipboard functionality
 * @param columnName - The name of the ID column (e.g., 'id', 'accountId', 'policyId')
 * @param title - The display title for the column
 * @param options - Additional column options
 * @returns Column configuration object
 */
export const createIdColumn = (
  columnName: string,
  title = 'ID',
  options: {
    show?: boolean;
    width?: string | number;
    fixed?: string;
    sort?: boolean;
    filterType?: string;
  } = {}
) => {
  return {
    name: columnName,
    title,
    show: options.show ?? false,
    width: options.width ?? 'l',
    fixed: options.fixed,
    sort: options.sort ?? true,
    filterType: options.filterType ?? 'string',
    getCellValue: (row: any) => row[columnName] || '-',
  };
};

/**
 * Helper function to create a custom cell component that uses IdTableCell for ID columns
 * @param columnName - The name of the column to check
 * @returns Cell component function
 */
export const createCellComponent = (columnName: string) => {
  return (props: any) => {
    // Check if this is an ID column (ends with 'Id' or is exactly 'id')
    const isIdColumn =
      columnName.toLowerCase().endsWith('id') ||
      columnName.toLowerCase() === 'id';

    if (isIdColumn) {
      return <IdTableCell {...props} />;
    }

    return <DefaultTableCell {...props} />;
  };
};
